import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ClearIcon from '@mui/icons-material/Clear';
import { Button, Checkbox, Chip, FormControl, FormGroup, FormLabel, Grid, TextField } from '@mui/material';

import CustomAutocomplete from 'common/components/CustomAutocomplete';
import useStylesForm from 'common/components/styles/useStylesForm.styles';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useStylesProductForm from '../styles/useStylesProductForm.styles';
import ProductFormVariants from './ProductFormVariants';

const ProductFormAddVariants = (props) => {
    const {
        variant,
        variants,
        setVariants,
        options,
        editingVariantIndex,
        closeForm,
        duplicateOptions,
        formConfigProductVariant,
        canEdit,
    } = props;
    const { t } = useTranslation();

    const { classes: classes1 } = useStylesForm();
    const { classes: classes2, cx } = useStylesProductForm();
    const classes = { ...classes1, ...classes2 };

    const { enqueueSnackbar } = useSnackbar();

    const [selectedValues, setSelectedValues] = useState([]);

    const [suggestions, setSuggestions] = useState([]);

    const [initialsSuggestions, setInitialsSuggestions] = useState([]);

    const [newVariant, setNewVariant] = useState(variant);

    const [showForm, setShowForm] = useState(false);

    const initializedOptions = useCallback(() => {
        resetForm();
        let suggestionsArr = [];
        options.forEach((o) => {
            o.values.forEach((v, i) => {
                let sug = {
                    name: o.name,
                    value: v,
                };
                suggestionsArr = [...suggestionsArr, sug];
            });
        });
        setInitialsSuggestions(suggestionsArr);
        setSuggestions(suggestionsArr);
    }, [options]);

    useEffect(() => {
        initializedOptions();
    }, [initializedOptions]);

    const resetForm = () => {
        setSuggestions([]);
        setInitialsSuggestions([]);
        setSelectedValues([]);
    };

    const handleTagChange = (values) => {
        setSelectedValues(values);
        let sug = [...initialsSuggestions];
        values.forEach((v, i) => {
            sug = sug.filter((s) => s.name !== v.name || s.value === v.value);
        });
        setSuggestions(sug);
    };

    const submit = () => {
        if (validateForm()) {
            setNewVariant({
                url: variant.url,
                images: [],
                currency: variant.currency,
                price: variant.price,
                discountType: variant.discountType,
                discount: variant.discount,
                stock: variant.stock,
                selectedOptions: selectedValues,
            });
            initializedOptions();
            closeForm(-2);
            setShowForm(true);
        }
    };

    const compareOptionsValues = (opt) => {
        let optionsTemp = [];
        optionsTemp = [...optionsTemp, ...opt];
        return (
            JSON.stringify(
                optionsTemp.sort((a, b) => {
                    return ('' + a.name).localeCompare(b.name);
                }),
            ) ===
            JSON.stringify(
                selectedValues.sort((a, b) => {
                    return ('' + a.name).localeCompare(b.name);
                }),
            )
        );
    };

    const validateForm = () => {
        if (selectedValues.length === 0) {
            enqueueSnackbar(t('product.productFormAddVariants.formError'), {
                variant: 'error',
            });
            return false;
        }

        let i = variants.findIndex((v) => compareOptionsValues(v.selectedOptions));

        if (i !== -1) {
            enqueueSnackbar(t('product.productFormAddVariants.variantSameOptionError'), {
                variant: 'error',
            });
            return false;
        }

        if (editingVariantIndex !== -1) {
            enqueueSnackbar(t('product.productFormAddVariants.variantOpenError'), {
                variant: 'error',
            });
            return false;
        }

        return true;
    };

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    return (
        <React.Fragment>
            {!duplicateOptions && (
                <div>
                    <FormLabel>{t('product.productFormAddVariants.add')}</FormLabel>
                    {!showForm && (
                        <div>
                            <Grid container spacing={1}>
                                <Grid item md={9}>
                                    <FormGroup>
                                        <FormControl className={classes.formControl}>
                                            <CustomAutocomplete
                                                value={selectedValues}
                                                onChange={(event, value) => {
                                                    handleTagChange(value);
                                                }}
                                                className={classes.autocomplete}
                                                multiple
                                                id="checkboxes-tags-demo"
                                                options={suggestions}
                                                disableCloseOnSelect
                                                size={'small'}
                                                getOptionLabel={(option) => option.value}
                                                renderTags={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => (
                                                        <Chip
                                                            key={option.value}
                                                            label={option.value}
                                                            deleteIcon={<ClearIcon />}
                                                            {...getTagProps({ index })}
                                                        />
                                                    ))
                                                }
                                                renderOption={(props, option, { selected }) => (
                                                    <li {...props}>
                                                        <Checkbox
                                                            icon={icon}
                                                            checkedIcon={checkedIcon}
                                                            style={{ marginRight: 8 }}
                                                            checked={selected}
                                                        />
                                                        {option.value}
                                                    </li>
                                                )}
                                                style={{ width: 500 }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        className={classes.textField}
                                                        variant="outlined"
                                                        label=""
                                                        placeholder=""
                                                    />
                                                )}
                                            />
                                        </FormControl>
                                    </FormGroup>
                                </Grid>
                                <Grid item md={3}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        type="submit"
                                        color="primary"
                                        className={cx(classes.buttonStyle, classes.saveButton)}
                                        disableElevation={true}
                                        onClick={submit}
                                    >
                                        {t('product.productFormAddVariants.save')}
                                    </Button>
                                </Grid>
                            </Grid>
                        </div>
                    )}
                    {showForm && (
                        <div className={cx(classes.variantContent, classes.mb20px)}>
                            <div className={classes.editVariant}>
                                <div className={classes.p10px}>
                                    <Grid container spacing={1} className={classes.optionsRow}>
                                        <Grid item md={6} className={cx(classes.variantOption)}>
                                            {newVariant.selectedOptions.map((option, idx) => (
                                                <span
                                                    key={option.value}
                                                    className={cx(classes.variantRowSpan, classes.varOptText)}
                                                >
                                                    {option.value +
                                                        (newVariant.selectedOptions.length - 1 > idx ? ' / ' : '')}
                                                </span>
                                            ))}
                                        </Grid>
                                    </Grid>
                                </div>
                            </div>
                            <div className={classes.p10px}>
                                <ProductFormVariants
                                    options={options}
                                    variant={newVariant}
                                    variants={variants}
                                    setVariants={(e) => {
                                        setVariants(e);
                                        setShowForm(false);
                                    }}
                                    close={() => {
                                        closeForm(-1);
                                        setShowForm(false);
                                    }}
                                    formConfigProductVariant={formConfigProductVariant}
                                    canEdit={canEdit}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </React.Fragment>
    );
};

export default ProductFormAddVariants;
